import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, Row, Col, Card, Button } from "react-bootstrap";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { beginPaximumTransaction, setReservationInfo } from "../../../services/paximumService";

import { goToTebPaymentPage } from "../../../common/methods";
import visaLogo from "../../../assets/img/visa.jpg";
import oneForLogo from "../../../assets/img/OneFor.png";
import passportIcon from "../../../assets/img/PassportIcon.png";
import starIcon from "../../../assets/img/star.png";
import TebLogo from "../../../assets/svg/TebLogo";
import MasterCardLogo from "../../../assets/svg/MasterCardLogo";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { IsMobileOrBrowser } from "../../../hooks/isBrowserOrMobile";
import countriesData from "../../../utils/countries-min.json";
import { countryTelData } from "../../../utils/countryTelData";
import Header from "../../../components/Header";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { countryCodesData, getDialCodeByCountryCode } from "../../../utils/countryCodesData";
import { fetchHotelOffersDetails } from "../../../utils/Hote";
import RefundIcon from "../../../assets/svg/Refund.svg";
import NonRefundIcon from "../../../assets/svg/NonRefund.svg";
import moment from 'moment';

const primaryColor = "#003663";

// Add Kosovo to countryTelData if not present
const extendedCountryTelData = {
  ...countryTelData,
  "XK": { name: "Kosovo", dial_code: "+383" }
};

// Simple expiration check functions
const isOfferExpired = (offer) => {
  if (!offer?.expiresOn) return false;
  return moment().isAfter(moment(offer.expiresOn));
};

const isSearchExpired = (searchExpiresOn) => {
  if (!searchExpiresOn) return false;
  return moment().isAfter(moment(searchExpiresOn));
};

// Helper function to convert country code to full name
const getCountryNameFromCode = (code) => {
  if (!code) return "";
  const country = countriesData.find(c => c.cca2 === code.toUpperCase());
  return country ? country.name.common : code;
};

// Helper function to ensure nationality is always a valid string
const validateNationalityCode = (nationality) => {
  if (typeof nationality === 'string' && nationality.length === 2) {
    return nationality.toUpperCase();
  }
  if (typeof nationality === 'object' && nationality?.twoLetterCode) {
    return String(nationality.twoLetterCode).toUpperCase();
  }
  console.warn('⚠️ Invalid nationality format, defaulting to XK:', nationality);
  return 'XK';
};

const textStyle = { color: primaryColor };

const hotelCardStyle = {
  backgroundColor: "#fff",
  borderRadius: "0 0 16px 16px",
  padding: "24px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  margin: "24px auto",
  width: "95%",
  maxWidth: "800px",
  borderTop: "none"
};

const inputStyle = {
  padding: "10px 15px",
  borderRadius: "12px",
  border: "1px solid #E5E9F2",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.05)",
  width: "100%",
  height: '54px',
  backgroundColor: '#F8FAFC',
  transition: 'all 0.2s ease-in-out',
  '&:focus': {
    borderColor: '#003663',
    backgroundColor: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
  }
};

// Update form label styles
const formLabelStyle = (hasError) => ({
  color: hasError ? '#dc3545' : '#003663',
  fontWeight: '500',
  marginBottom: '8px',
  fontSize: '1rem',
  transition: 'color 0.2s ease-in-out',
  borderBottom: hasError ? '2px solid #dc3545' : 'none',
  display: 'inline-block',
  paddingBottom: '4px'
});

// Update error message style
const errorMessageStyle = {
  color: '#dc3545',
  fontSize: '0.875rem',
  marginTop: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  gap: '6px'
};

// Update nationality dropdown styles
const nationalityDropdownStyle = {
  position: 'fixed', // Use fixed positioning to escape container bounds
  backgroundColor: '#fff',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  zIndex: 9999999999, // Extremely high z-index to ensure it's always on top
  maxHeight: '500px', // Significantly increased height to show more options
  overflow: 'hidden',
  display: 'none', // Force closed by default
  border: '1px solid #E5E9F2',
  width: '350px', // Increased width for better readability
  maxWidth: '90vw' // Responsive max width
};

const nationalityOptionStyle = (isSelected) => ({
  padding: '14px 18px', // Increased padding for better touch targets
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: isSelected ? '#003663' : 'transparent',
  color: isSelected ? '#fff' : '#1a1a1a', // Changed to darker color for better visibility
  fontSize: '15px', // Slightly larger font
  fontWeight: '500', // Medium weight for better readability
  borderBottom: '1px solid #f0f0f0', // Add subtle separator
  '&:hover': {
    backgroundColor: isSelected ? '#003663' : '#F0F7FF'
  }
});

// Add error icon component
const ErrorIcon = () => (
  <div style={{
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#dc3545',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#fff'
  }}>
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="9" stroke="#dc3545" strokeWidth="2"/>
      <path d="M10 5v6" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="10" cy="14" r="1" fill="#dc3545"/>
    </svg>
  </div>
);

// Add custom styles for the date picker
const datePickerCustomStyles = `
  <style>
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .birth-date-input-wrapper {
      position: relative;
      width: 100%;
      display: block;
    }

    .birth-date-input {
      width: 100% !important;
      display: block !important;
      height: 54px;
      padding: 12px 48px 12px 16px;
      border-radius: 12px;
      border: 1px solid #E5E9F2;
      background-color: #F8FAFC;
      color: #003663;
      font-size: 16px;
      font-weight: 500;
      transition: all 0.2s ease-in-out;
      cursor: pointer;
    }

    .birth-date-input:hover {
      border-color: #003663;
      background-color: #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .birth-date-input:focus {
      border-color: #003663;
      background-color: #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      outline: none;
    }

    .birth-date-input.error {
      border-color: #dc3545;
      padding-right: 4rem;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right calc(0.375em + 0.1875rem) center;
      background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
    }

    .form-control.error {
      border-color: #dc3545;
      padding-right: 4rem;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right calc(0.375em + 0.1875rem) center;
      background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
    }

    .form-label.error {
      color: #dc3545;
    }

    .error-message {
      color: #dc3545;
      font-size: 0.875em;
      margin-top: 0.25rem;
    }

    .react-datepicker {
      font-family: inherit !important;
      border: none !important;
      border-radius: 12px !important;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
      padding: 16px !important;
    }

    .react-datepicker__header {
      background-color: #fff !important;
      border-bottom: 1px solid #E5E9F2 !important;
      border-radius: 12px 12px 0 0 !important;
      padding-top: 16px !important;
      position: relative !important;
    }

    .react-datepicker__day--outside-month {
      visibility: hidden !important;
      pointer-events: none !important;
    }

    .calendar-icon {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      color: #003663;
      display: flex;
      alignItems: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      z-index: 1;
    }

    .react-datepicker__current-month {
      color: #003663 !important;
      font-weight: 600 !important;
      font-size: 16px !important;
      margin-bottom: 8px !important;
    }

    .react-datepicker__navigation {
      top: 16px !important;
    }

    .react-datepicker__day-name {
      color: #003663 !important;
      font-weight: 500 !important;
      margin: 8px !important;
      width: 32px !important;
      font-size: 14px !important;
    }

    .react-datepicker__day {
      margin: 8px !important;
      width: 32px !important;
      height: 32px !important;
      line-height: 32px !important;
      border-radius: 50% !important;
      color: #003663 !important;
      font-size: 14px !important;
      transition: all 0.2s ease-in-out !important;
    }

    .react-datepicker__day:hover {
      background-color: #F0F7FF !important;
    }

    .react-datepicker__day--selected {
      background-color: #003663 !important;
      color: #fff !important;
      font-weight: 600 !important;
    }

    .react-datepicker__day--keyboard-selected {
      background-color: #F0F7FF !important;
      color: #003663 !important;
    }

    .react-datepicker__year-select,
    .react-datepicker__month-select {
      padding: 8px !important;
      border-radius: 8px !important;
      border: 1px solid #E5E9F2 !important;
      background-color: #F8FAFC !important;
      color: #003663 !important;
      font-size: 14px !important;
      font-weight: 500 !important;
      cursor: pointer !important;
      transition: all 0.2s ease-in-out !important;
    }

    .react-datepicker__year-select:hover,
    .react-datepicker__month-select:hover {
      border-color: #003663 !important;
      background-color: #fff !important;
    }

    .react-datepicker__year-select:focus,
    .react-datepicker__month-select:focus {
      outline: none !important;
      border-color: #003663 !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    }

    .react-datepicker-wrapper,
    .react-datepicker__input-container {
      width: 100% !important;
      display: block !important;
    }

    .react-datepicker-popper {
      margin-left: 32px !important;
      z-index: 10;
    }

    .custom-header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 12px;
      margin-bottom: 12px;
    }

    .custom-header-selectors {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .custom-dropdown-button {
      background: #F8FAFC;
      border: 1px solid #E5E9F2;
      border-radius: 8px;
      padding: 4px 12px;
      color: #003663;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      min-width: 100px;
      text-align: center;
    }

    .custom-dropdown-button:hover {
      background: #fff;
      border-color: #003663;
    }

    .custom-nav-button {
      background: none;
      border: none;
      color: #003663;
      cursor: pointer;
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s ease-in-out;
    }

    .custom-nav-button:hover {
      background: #F0F7FF;
    }

    .custom-nav-button:disabled {
      color: #ccc;
      cursor: not-allowed;
    }

    .year-dropdown {
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      max-height: 300px;
      overflow-y: auto;
      z-index: 10;
      width: 200px;
      padding: 8px;
      margin-top: 4px;
      display: none;
    }

    .year-dropdown.show {
      display: block;
    }

    .decade-group {
      border-bottom: 1px solid #E5E9F2;
      padding: 8px 0;
    }

    .decade-group:last-child {
      border-bottom: none;
    }

    .decade-label {
      color: #003663;
      font-weight: 600;
      padding: 4px 8px;
      font-size: 12px;
    }

    .year-option {
      padding: 6px 12px;
      cursor: pointer;
      color: #003663;
      border-radius: 6px;
      transition: all 0.2s ease-in-out;
      font-size: 14px;
    }

    .year-option:hover {
      background: #F0F7FF;
    }

    .year-option.selected {
      background: #003663;
      color: #fff;
    }

    .month-dropdown {
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      z-index: 10;
      width: 160px;
      padding: 8px;
      margin-top: 4px;
      display: none;
    }

    .month-dropdown.show {
      display: block;
    }

    .month-option {
      padding: 6px 12px;
      cursor: pointer;
      color: #003663;
      border-radius: 6px;
      transition: all 0.2s ease-in-out;
      font-size: 14px;
    }

    .month-option:hover {
      background: #F0F7FF;
    }

    .month-option.selected {
      background: #003663;
      color: #fff;
    }

    .nationality-select-container {
      position: relative;
      width: 100%;
    }

    .nationality-select-input {
      height: 54px;
      padding: 12px 48px 12px 16px;
      border-radius: 12px;
      border: 1px solid #E5E9F2;
      background-color: #F8FAFC;
      color: #003663;
      font-size: 16px;
      font-weight: 500;
      width: 100%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: all 0.2s ease-in-out;
    }

    .nationality-select-input:hover {
      border-color: #003663;
      background-color: #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .nationality-select-input.error {
      border-color: #dc3545;
    }

    .nationality-select-dropdown {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      right: 0;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      max-height: 300px;
      overflow-y: auto;
      z-index: 1000;
      display: none;
      border: 1px solid #E5E9F2;
    }

    .nationality-select-dropdown.open {
      display: block;
    }

    .nationality-option {
      padding: 12px 16px;
      cursor: pointer;
      color: #003663;
      transition: all 0.2s ease-in-out;
      font-size: 14px;
    }

    .nationality-option:hover {
      background-color: #F0F7FF;
    }

    .nationality-option.selected {
      background-color: #003663;
      color: white;
      font-weight: 500;
    }

    .nationality-select-arrow {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
      transition: transform 0.2s ease-in-out;
    }

    .nationality-select-arrow.open {
      transform: translateY(-50%) rotate(180deg);
    }

    .phone-code-select-container {
      position: relative;
      width: 120px;
    }

    .phone-code-select-input {
      height: 54px;
      padding: 8px 32px 8px 12px;
      border-radius: 12px;
      border: 1px solid #E5E9F2;
      background-color: #F8FAFC;
      color: #003663;
      font-size: 14px;
      font-weight: 500;
      width: 100%;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.2s ease-in-out;
    }

    .phone-code-select-input:hover {
      border-color: #003663;
      background-color: #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .phone-code-select-input .flag {
      width: 20px;
      height: 15px;
      border-radius: 2px;
      object-fit: cover;
    }

    .phone-code-select-dropdown {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      max-height: 300px;
      overflow-y: auto;
      z-index: 1000;
      display: none;
      border: 1px solid #E5E9F2;
      width: 180px;
    }

    .phone-code-select-dropdown.open {
      display: block;
    }

    .phone-code-option {
      padding: 8px 12px;
      cursor: pointer;
      color: #003663;
      transition: all 0.2s ease-in-out;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .phone-code-option:hover {
      background-color: #F0F7FF;
    }

    .phone-code-option.selected {
      background-color: #003663;
      color: white;
      font-weight: 500;
    }

    .phone-code-option .flag {
      width: 20px;
      height: 15px;
      border-radius: 2px;
      object-fit: cover;
    }

    .phone-code-select-arrow {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      transition: transform 0.2s ease-in-out;
    }

    .phone-code-select-arrow.open {
      transform: translateY(-50%) rotate(180deg);
    }
  </style>
`;

/** Custom Header Component for DatePicker */
const CustomHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  t
}) => {
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const yearDropdownRef = useRef(null);
  const monthDropdownRef = useRef(null);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();
  const today = new Date();
  const isCurrentMonth = currentYear === today.getFullYear() && currentMonth === today.getMonth();

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 1900; year--) {
      years.push(year);
    }
    return years;
  };

  const years = generateYears();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setShowYearDropdown(false);
      }
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) {
        setShowMonthDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="custom-header-container">
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className="custom-nav-button"
      >
        {"<"}
      </button>

      <div className="custom-header-selectors">
        <div style={{ position: 'relative' }}>
          <button
            className="custom-dropdown-button"
            onClick={() => {
              setShowMonthDropdown(!showMonthDropdown);
              setShowYearDropdown(false);
            }}
          >
            {t(months[currentMonth])}
          </button>
          
          <div 
            ref={monthDropdownRef}
            className={`month-dropdown ${showMonthDropdown ? 'show' : ''}`}
          >
            {months.map((month, idx) => (
              <div
                key={month}
                className={`month-option ${idx === currentMonth ? 'selected' : ''}`}
                onClick={() => {
                  changeMonth(idx);
                  setShowMonthDropdown(false);
                }}
              >
                {t(month)}
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <button
            className="custom-dropdown-button"
            onClick={() => {
              setShowYearDropdown(!showYearDropdown);
              setShowMonthDropdown(false);
            }}
          >
            {currentYear}
          </button>
          
          <div 
            ref={yearDropdownRef}
            className={`year-dropdown ${showYearDropdown ? 'show' : ''}`}
          >
            {years.map((year) => (
              <div
                key={year}
                className={`year-option ${year === currentYear ? 'selected' : ''}`}
                onClick={() => {
                  changeYear(year);
                  setShowYearDropdown(false);
                }}
              >
                {year}
              </div>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled || isCurrentMonth}
        className="custom-nav-button"
        style={{ visibility: isCurrentMonth ? 'hidden' : 'visible' }}
      >
        {">"}
      </button>
    </div>
  );
};



const BookingPage = ({
  selectedRooms,
  totalPrice,
  hotelName,
  hotelLocation,
  checkIn,
  checkOut,
  thumbnail,
  nights,
  hotelStars,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [reservationNote, setReservationNote] = useState('');
  const [travelers, setTravelers] = useState([]);
  const [birthDays, setBirthDays] = useState({});
  const [birthMonths, setBirthMonths] = useState({});
  const [birthYears, setBirthYears] = useState({});
  const location = useLocation();
  const locationState = location.state || {};
  const isMobile = IsMobileOrBrowser();
  
  // Add ref for nationality dropdown
  const nationalityDropdownRef = useRef(null);
  
  // Extract nationality from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const nationalityFromUrl = urlParams.get('nationality');
  
  // Debug log
  console.log('Nationality from URL:', nationalityFromUrl);
  console.log('Location state:', locationState);

  // Gjenero datat fallback për shembull
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const defaultCheckIn = today.toISOString().split('T')[0];
  const defaultCheckOut = tomorrow.toISOString().split('T')[0];
  
  // Përdor vlerat nga props ose location.state nëse nuk janë përcaktuar
  const effectiveCheckIn = checkIn || locationState.checkIn || locationState.checkInDate || defaultCheckIn;
  const effectiveCheckOut = checkOut || locationState.checkOut || locationState.checkOutDate || defaultCheckOut;

  // Debug logging for dates
  console.log("🏨 Booking Page - Date sources:", {
    checkIn,
    checkOut,
    "locationState.checkIn": locationState.checkIn,
    "locationState.checkOut": locationState.checkOut,
    "locationState.checkInDate": locationState.checkInDate,
    "locationState.checkOutDate": locationState.checkOutDate,
    defaultCheckIn,
    defaultCheckOut,
    effectiveCheckIn,
    effectiveCheckOut
  });
  const effectiveNights = nights || locationState.nights || 
    (effectiveCheckIn && effectiveCheckOut ? 
      Math.round((new Date(effectiveCheckOut) - new Date(effectiveCheckIn)) / (1000 * 60 * 60 * 24)) : 
      1);
  
  // Debug log për të kuptuar nga ku po vijnë datat
  useEffect(() => {
    console.log("Props checkIn:", checkIn);
    console.log("Props checkOut:", checkOut);
    console.log("Props hotelStars:", hotelStars);
    console.log("Location state:", location.state);
    console.log("Effective dates:", { 
      checkIn: effectiveCheckIn, 
      checkOut: effectiveCheckOut,
      nights: effectiveNights
    });
  }, []);

  // Shto debugger log për thumbnail
  useEffect(() => {
    console.log("BookingPage Props:", {
      selectedRooms,
      totalPrice,
      hotelName,
      hotelLocation,
      checkIn,
      checkOut,
      nights,
      thumbnail
    });
  }, []);

  // Date card styles with enhanced shadow
  const dateCardStyle = {
    flex: 1,
    backgroundColor: "#fff",
    borderRadius: "0 0 16px 16px",
    padding: "16px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    textAlign: "center",
    margin: "8px",
    borderTop: "none",
    position: "relative",
    overflow: "hidden"
  };

  const blendOverlayStyle = {
    position: "absolute",
    top: "-20px",
    left: 0,
    right: 0,
    height: "20px",
    background: "linear-gradient(to bottom, #f8f9fa, #fff)",
    zIndex: 1
  };

  const dateHeaderStyle = {
    fontSize: "1rem",
    fontWeight: 600,
    color: primaryColor,
    marginBottom: "12px",
  };

  const dateLabelStyle = {
    fontSize: "1.2rem",
    fontWeight: 500,
    color: primaryColor,
  };

  const dateDetailStyle = {
    fontSize: "1rem",
    color: primaryColor,
    fontWeight: 500,
  };
  
  // Payment method tab styles
  const paymentTabStyle = {
    marginBottom: "20px",
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fff",
    padding: "10px 15px",
    borderRadius: "0 0 8px 8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    borderTop: "none"
  };

  const paymentHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const arpaymentHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  // Promo code states - moved before calculations
  const [promoCode, setPromoCode] = useState(false);
  const [promoCodeValue, setPromoCodeValue] = useState("");
  const [voucherInfo, setVoucherInfo] = useState({
    message: "",
    code: 0,
    loading: false,
  });
  const [discountAmount, setDiscountAmount] = useState(0);

  const defaultRoom = {
    roomName: t("beach_villa"),
    boardType: t("full_board"),
    price: { amount: 164.8, currency: "EUR" },
    offerId: "",
  };

  const rooms = selectedRooms?.length ? selectedRooms : [defaultRoom];
  const baseTotal = totalPrice || rooms.reduce((acc, r) => acc + (r.price?.amount || 0), 0);
  const overallTotal = Math.max(0, baseTotal - discountAmount); // Ensure total doesn't go below 0
  const currency = rooms[0]?.price?.currency || "EUR";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [guestFirstName, setGuestFirstName] = useState("");
  const [guestLastName, setGuestLastName] = useState("");
  const [bookingForOther, setBookingForOther] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [disableBookingBtn, setDisableBookingBtn] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [oneForSessionId, setOneForSessionId] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [transactionId, setTransactionId] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [isNationalityOpen, setIsNationalityOpen] = useState(false);
  const [isPhoneCodeOpen, setIsPhoneCodeOpen] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Move state to component level
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [addressCountrySearchQuery, setAddressCountrySearchQuery] = useState('');
  const [isAddressCountryOpen, setIsAddressCountryOpen] = useState(false);

  // Add months array at component level
  const months = [
    t("january"), t("february"), t("march"), t("april"), t("may"), t("june"),
    t("july"), t("august"), t("september"), t("october"), t("november"), t("december")
  ];

  // Add isValidDate function at component level
  const isValidDate = (dateString) => {
    if (!dateString) return false;
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date instanceof Date && !isNaN(date) && 
           date <= new Date() && 
           date.getDate() === day &&
           date.getMonth() === month - 1 &&
           date.getFullYear() === year;
  };

  // Filter countries based on search query at component level
  const filteredCountries = useMemo(() => {
    const priorityCountries = ['XK', 'AL']; // Kosovo and Albania country codes
    
    const filtered = countriesData
      .filter(country => 
        country.name.common.toLowerCase().includes(countrySearchQuery.toLowerCase())
      );

    // Sort with priority countries first, then alphabetically
    return filtered.sort((a, b) => {
      const aIndex = priorityCountries.indexOf(a.cca2);
      const bIndex = priorityCountries.indexOf(b.cca2);
      
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      return a.name.common.localeCompare(b.name.common);
    });
  }, [countrySearchQuery]);

  // Filter countries for address dropdown
  const filteredAddressCountries = useMemo(() => {
    const priorityCountries = ['XK', 'AL']; // Kosovo and Albania country codes
    
    const filtered = countriesData
      .filter(country => 
        country.name.common.toLowerCase().includes(addressCountrySearchQuery.toLowerCase())
      );

    // Sort with priority countries first, then alphabetically
    return filtered.sort((a, b) => {
      const aIndex = priorityCountries.indexOf(a.cca2);
      const bIndex = priorityCountries.indexOf(b.cca2);
      
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      return a.name.common.localeCompare(b.name.common);
    });
  }, [addressCountrySearchQuery]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const weekdays = [t('sunday'), t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday')];
      const months = [t('january'), t('february'), t('march'), t('april'), t('may'), t('june'), t('july'), t('august'), t('september'), t('october'), t('november'), t('december')];
      
      return {
        weekday: weekdays[date.getDay()],
        day: date.getDate(),
        month: months[date.getMonth()],
        year: date.getFullYear()
      };
    } catch (error) {
      console.error('Date formatting error:', error);
      return {
        weekday: '',
        day: '',
        month: '',
        year: ''
      };
    }
  };

  const countTravelers = (room) => {
    if (room?.assignedCriteria) {
      const totalAdults = room.assignedCriteria.adult || 0;
      const totalChildren = room.assignedCriteria.childAges?.length || 0;
      return totalAdults + totalChildren;
    }
    return room?.rooms?.[0]?.travellers?.length || 0;
  };

  const extractRoomName = (room) => {
    const roomName = room?.rooms?.[0]?.roomName || room?.roomName;
    
    if (!roomName) return t("n_a");
    
    // Check if it looks like a translation key (lowercase with underscores)
    if (typeof roomName === 'string' && /^[a-z_]+$/.test(roomName)) {
      return t(roomName);
    }
    
    // Otherwise render directly
    return roomName;
  };

  const renderTravelerDetails = (room) => {
    if (!room?.assignedCriteria) return null;

    const { adult, childAges } = room.assignedCriteria;
    return (
      <div style={{ fontSize: "1rem", textAlign: 'left', marginTop: "8px" }}>
        <div>{adult} {adult === 1 ? t("Adult") : t("Adults")}</div>
        {childAges && childAges.length > 0 && (
          <div>
            {childAges.length} {childAges.length === 1 ? t("Child") : t("Children")}
            {childAges.length > 0 && (
              <div style={{ fontSize: "0.9rem", color: "#666", marginTop: "4px" }}>
                {t("Ages")}: {childAges.join(", ")}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const initializeTravelers = () => {
      const newTravelers = [];
      const defaultNationality = nationalityFromUrl || locationState.nationality || "XK";
      const defaultNationalityName = getCountryNameFromCode(defaultNationality);
      
      console.log('Using nationality:', defaultNationality);
      console.log('Nationality name:', defaultNationalityName);
      
      rooms.forEach((room, roomIndex) => {
        const adultsCount = room.assignedCriteria?.adult || 0;
        const childrenCount = room.assignedCriteria?.childAges?.length || 0;
        
        // Shtojmë të rriturit
        for (let i = 0; i < adultsCount; i++) {
          const defaultTitle = 1; // Mr by default
          newTravelers.push({
            type: 1, // Adult në Traveller Types
            title: defaultTitle,
            passengerType: 1, // Adult në Passenger Types
            isLeader: roomIndex === 0 && i === 0,
            name: "",
            surname: "",
            birthDate: "",
            nationality: { 
              twoLetterCode: defaultNationality,
              name: defaultNationalityName
            },
            email: "",
            phone: "",
            gender: defaultTitle === 1 ? 1 : 0, // Set gender based on title (Mr = Male = 1, Mrs = Female = 0)
          });
        }
        
        if (room.assignedCriteria?.childAges) {
          room.assignedCriteria.childAges.forEach((age) => {
            newTravelers.push({
              type: 2, // Child në Traveller Types
              title: 5, // Child në Traveller Titles
              passengerType: 2, // Child në Passenger Types
              isLeader: false,
              name: "",
              surname: "",
              birthDate: "",
              nationality: { 
                twoLetterCode: defaultNationality,
                name: defaultNationalityName
              },
              gender: "", // Leave empty for children
              age: age
            });
          });
        }
      });
      setTravelers(newTravelers);
    };

    initializeTravelers();
  }, [rooms, locationState.nationality, nationalityFromUrl]);

  const handleTravelerChange = (index, field, value) => {
    console.log('Changing traveler:', { index, field, value }); // Debug log
    const updatedTravelers = [...travelers];
    
    if (field === "title") {
      // When title changes, automatically set gender for adults
      const isChild = updatedTravelers[index].type === 2;
      if (!isChild) {
        // Mr = 1 (Male), Mrs = 3 (Female)
        const gender = value === 1 ? 1 : 0;
        updatedTravelers[index] = {
          ...updatedTravelers[index],
          title: value,
          gender: gender
        };
      } else {
        updatedTravelers[index] = {
          ...updatedTravelers[index],
          title: value
        };
      }
    } else if (field === "addressDetails") {
      // Handle address-related updates
      updatedTravelers[index] = {
        ...updatedTravelers[index],
        address: {
          ...updatedTravelers[index].address,
          ...value
        }
      };
    } else if (field === "phone") {
      // Handle phone number update - store in both places for compatibility
      const countryCode = extendedCountryTelData[updatedTravelers[index].nationality?.twoLetterCode]?.dial_code.replace("+", "") || "49";
      updatedTravelers[index] = {
        ...updatedTravelers[index],
        phone: value, // Store directly for input binding
        address: {
          ...updatedTravelers[index].address,
          contactPhone: {
            countryCode: countryCode,
            phoneNumber: value
          }
        }
      };
    } else {
      updatedTravelers[index] = {
        ...updatedTravelers[index],
        [field]: value
      };
    }
    
    setTravelers(updatedTravelers);
  };

  const handleNationalitySelect = (countryCode, index, traveler) => {
    const country = countriesData.find(c => c.cca2 === countryCode);
    handleTravelerChange(index, "nationality", { 
      twoLetterCode: countryCode,
      name: country?.name.common || ""
    });
    
    // Update phone code for lead passenger when nationality changes
    if (traveler.isLeader) {
      // Update the phone code but keep the existing phone number
      const currentPhone = traveler.phone || "";
      // Remove any existing country code from the phone number
      const phoneWithoutCode = currentPhone.replace(/^\+?\d{1,4}\s?/, "");
      const newPhoneCode = extendedCountryTelData[countryCode]?.dial_code || "+49";
      
      // Update the phone with new country code
      handleTravelerChange(index, "phone", phoneWithoutCode);
      
      // Also update address country if not already set
      if (!traveler.address?.country?.id) {
        handleTravelerChange(index, "addressDetails", {
          country: {
            id: countryCode,
            name: country?.name.common || ""
          }
        });
      }
    }
    setIsNationalityOpen(false);
  };

  const handleBirthDateChange = (index, type, value) => {
    let newDay = birthDays[index] || '';
    let newMonth = birthMonths[index] || '';
    let newYear = birthYears[index] || '';

    switch(type) {
      case 'day':
        if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 31)) {
          newDay = value;
          setBirthDays(prev => ({ ...prev, [index]: value }));
        }
        break;
      case 'month':
        newMonth = value;
        setBirthMonths(prev => ({ ...prev, [index]: value }));
        break;
      case 'year':
        if (value === '' || (value.length <= 4 && /^\d*$/.test(value))) {
          newYear = value;
          setBirthYears(prev => ({ ...prev, [index]: value }));
        }
        break;
    }

    // Only update the full date if all parts are valid
    if (newDay && newMonth && newYear && newYear.length === 4) {
      const monthIndex = months.indexOf(newMonth);
      const formattedDate = `${newYear}-${String(monthIndex + 1).padStart(2, '0')}-${String(newDay).padStart(2, '0')}`;
      if (isValidDate(formattedDate)) {
        handleTravelerChange(index, "birthDate", formattedDate);
      }
    }
  };

  const renderTravelerForm = (traveler, index) => {
    const isLeader = traveler.isLeader;
    const isChild = traveler.type === 2;
    const selectedCountry = traveler.nationality?.twoLetterCode || "XK";
    const selectedAddressCountry = traveler.address?.country?.id || selectedCountry;
    const countryPhoneCode = extendedCountryTelData[selectedCountry]?.dial_code || "+49";
    
    const months = [
      t("january"), t("february"), t("march"), t("april"), t("may"), t("june"),
      t("july"), t("august"), t("september"), t("october"), t("november"), t("december")
    ];

    const hasError = (field) => {
      if (!showErrors) return false;
      if (field === 'nationality') return false; // Skip nationality validation since it's auto-set
      if (!traveler[field]) {
        return true;
      }
      if (field === 'email' && isLeader) {
        return !/^\S+@\S+$/i.test(traveler[field]);
      }
      return false;
    };

    const isValidEmail = (email) => {
      return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const selectedCountryName = countriesData.find(c => c.cca2 === selectedCountry)?.name.common || t("Select nationality");

    return (
      <div key={index} className="mb-5 p-4" style={{ 
        backgroundColor: "#fff", 
        borderRadius: "0 0 12px 12px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        borderTop: "none",
        position: "relative",
        overflow: "visible", // Allow dropdowns to show outside container
        zIndex: 1 // Base z-index for the container
      }}>
        <div style={blendOverlayStyle} />
        <div style={{position: "relative", zIndex: 1}}>
          <h5 className="mb-4" style={{ color: primaryColor }}>
            {isLeader ? t("lead_passenger") : isChild ? t("child") : t("Adult")} {index + 1}
            {isChild && ` (${t("Age")}: ${traveler.age})`}
          </h5>

          {!isChild && (
            <div className="mb-4">
              <h4 style={{ 
                fontWeight: '600',
                color: '#14284B',
                marginBottom: '32px',
                fontFamily: 'Poppins, sans-serif'
              }}>
                {t("passenger_details")}
              </h4>
              <div className="col-lg-12 d-flex mb-4 col-md-12 col-12">
                <div className="custom-control custom-radio custom-control border-bottom-0 tick col-auto">
                  <input
                    type="radio"
                    id={`customRadio-filter-Mr-${index}`}
                    value="mr"
                    required
                    name={`customRadio-${index}`}
                    className="pointer custom-control-input"
                    checked={traveler.title === 1}
                    onChange={() => handleTravelerChange(index, "title", 1)}
                  />
                  <label
                    className="custom-control-label d-flex align-items-center"
                    htmlFor={`customRadio-filter-Mr-${index}`}
                  >
                    {t("Mr")}
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control border-bottom-0 tick col-auto">
                  <input
                    type="radio"
                    id={`customRadio-filter-Ms-${index}`}
                    name={`customRadio-${index}`}
                    className="pointer custom-control-input"
                    value="ms"
                    checked={traveler.title === 3}
                    onChange={() => handleTravelerChange(index, "title", 3)}
                  />
                  <label
                    className="custom-control-label d-flex align-items-center"
                    htmlFor={`customRadio-filter-Ms-${index}`}
                  >
                    {t("Ms")}
                  </label>
                </div>
              </div>
            </div>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-4">
                <Form.Label style={formLabelStyle(hasError('name'))}>
                  {t("First Name")} {hasError('name') && '*'}
                </Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type="text"
                    value={traveler.name}
                    onChange={(e) => handleTravelerChange(index, "name", e.target.value)}
                    style={{
                      ...inputStyle,
                      borderColor: hasError('name') ? '#dc3545' : '#E5E9F2',
                      borderWidth: hasError('name') ? '2px' : '1px'
                    }}
                    placeholder={t("Enter first name")}
                  />
                  {hasError('name') && <ErrorIcon />}
                </div>
                {hasError('name') && (
                  <div style={errorMessageStyle}>
                    <span>{t("Please fill this field")}</span>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-4">
                <Form.Label style={formLabelStyle(hasError('surname'))}>
                  {t("Last Name")} {hasError('surname') && '*'}
                </Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type="text"
                    value={traveler.surname}
                    onChange={(e) => handleTravelerChange(index, "surname", e.target.value)}
                    style={{
                      ...inputStyle,
                      borderColor: hasError('surname') ? '#dc3545' : '#E5E9F2',
                      borderWidth: hasError('surname') ? '2px' : '1px'
                    }}
                    placeholder={t("Enter last name")}
                  />
                  {hasError('surname') && <ErrorIcon />}
                </div>
                {hasError('surname') && (
                  <div style={errorMessageStyle}>
                    <span>{t("Please fill this field")}</span>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-4">
                <Form.Label style={formLabelStyle(hasError('birthDate'))}>
                  {t("Date of birth")} {hasError('birthDate') && '*'}
                </Form.Label>
                <div style={{ display: 'flex', gap: '10px', position: 'relative' }}>
                  <div style={{ width: '25%', position: 'relative' }}>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={birthDays[index] || ''}
                      onChange={(e) => handleBirthDateChange(index, 'day', e.target.value)}
                      placeholder="DD"
                      style={{
                        ...inputStyle,
                        borderColor: hasError('birthDate') ? '#dc3545' : '#E5E9F2',
                        borderWidth: hasError('birthDate') ? '2px' : '1px'
                      }}
                      maxLength="2"
                    />
                    {hasError('birthDate') && <ErrorIcon />}
                  </div>
                  <div style={{ width: '50%', position: 'relative' }}>
                    <Form.Select
                      value={birthMonths[index] || ''}
                      onChange={(e) => {
                        handleBirthDateChange(index, 'month', e.target.value);
                        const yearInput = document.querySelector(`#year-input-${index}`);
                        if (yearInput) yearInput.focus();
                      }}
                      style={{
                        ...inputStyle,
                        borderColor: hasError('birthDate') ? '#dc3545' : '#E5E9F2',
                        borderWidth: hasError('birthDate') ? '2px' : '1px'
                      }}
                    >
                      <option value="">{t("Month")}</option>
                      {months.map((month) => (
                        <option key={month} value={month}>{t(month)}</option>
                      ))}
                    </Form.Select>
                    {hasError('birthDate') && <ErrorIcon />}
                  </div>
                  <div style={{ width: '25%', position: 'relative' }}>
                    <Form.Control
                      id={`year-input-${index}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={birthYears[index] || ''}
                      onChange={(e) => handleBirthDateChange(index, 'year', e.target.value)}
                      placeholder="YYYY"
                      style={{
                        ...inputStyle,
                        borderColor: hasError('birthDate') ? '#dc3545' : '#E5E9F2',
                        borderWidth: hasError('birthDate') ? '2px' : '1px'
                      }}
                      maxLength="4"
                    />
                    {hasError('birthDate') && <ErrorIcon />}
                  </div>
                </div>
                {hasError('birthDate') && (
                  <div style={errorMessageStyle}>
                    <span>{t("Please enter a valid date")}</span>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          {isLeader && (
            <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label style={formLabelStyle(hasError('email'))}>
                      {t("Email")} {hasError('email') && '*'}
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type="email"
                        value={traveler.email}
                        onChange={(e) => handleTravelerChange(index, "email", e.target.value)}
                        style={{
                          ...inputStyle,
                          borderColor: hasError('email') ? '#dc3545' : '#E5E9F2',
                          borderWidth: hasError('email') ? '2px' : '1px'
                        }}
                        placeholder={t("Enter email address")}
                      />
                      {hasError('email') && <ErrorIcon />}
                    </div>
                    {hasError('email') && (
                      <div style={errorMessageStyle}>
                        <span>{t("Please enter a valid email address")}</span>
                      </div>
                    )}
                    {!hasError('email') && isValidEmail(traveler.email) && (
                      <div style={{
                        color: '#28a745',
                        fontSize: '0.875rem',
                        marginTop: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="9" stroke="#28a745" strokeWidth="2"/>
                          <path d="M6 10l3 3 6-6" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{t("Confirmation will be sent to this email")}</span>
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label style={formLabelStyle(hasError('phone'))}>
                      {t("Phone")} {hasError('phone') && '*'}
                    </Form.Label>
                    <div style={{ display: 'flex', gap: '10px', position: 'relative' }}>
                      {/* Phone Code Dropdown */}
                      <div style={{ width: '130px', position: 'relative' }}>
                        <div 
                          className="phone-code-select-input"
                          onClick={(e) => togglePhoneCodeDropdown(index, e)}
                          style={{
                            ...inputStyle,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            height: '54px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img 
                              src={`https://flagcdn.com/w20/${selectedCountry.toLowerCase()}.png`}
                              alt="flag"
                              style={{ width: 20, height: 15 }}
                            />
                            <span>{extendedCountryTelData[selectedCountry]?.dial_code || '+49'}</span>
                          </div>
                          <svg 
                            width="12" 
                            height="12" 
                            viewBox="0 0 16 16"
                            style={{
                              transform: openPhoneCodeDropdowns[index] ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.2s ease'
                            }}
                          >
                            <path 
                              fill="none" 
                              stroke="currentColor" 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth="2" 
                              d="M2 5l6 6 6-6"
                            />
                          </svg>
                        </div>
                        
                        {/* Phone Code Dropdown Menu */}
                        <div 
                          ref={el => phoneCodeDropdownRefs.current[index] = el}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '0',
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            zIndex: 9999999999,
                            width: '280px',
                            maxWidth: '90vw',
                            maxHeight: '500px',
                            overflow: 'hidden',
                            display: openPhoneCodeDropdowns[index] ? 'block' : 'none',
                            border: '1px solid #E5E9F2',
                            marginTop: '4px'
                          }}
                        >
                          <div style={{ padding: '16px', borderBottom: '1px solid #eef1f5' }}>
                            <Form.Control
                              type="text"
                              value={phoneCodeSearchQuery}
                              onChange={(e) => setPhoneCodeSearchQuery(e.target.value)}
                              placeholder={t("Search country or code...")}
                              style={{
                                ...inputStyle,
                                height: '44px',
                                boxShadow: 'none',
                                border: '1px solid #eef1f5'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div style={{ maxHeight: '440px', overflowY: 'auto' }}>
                            {filteredPhoneCodes.map(([code, data]) => (
                              <div
                                key={code}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePhoneCodeSelect(code, index, traveler);
                                }}
                                style={{
                                  padding: '14px 18px', // Increased padding for better touch targets
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  transition: 'background-color 0.2s',
                                  backgroundColor: code === selectedCountry ? '#003663' : 'transparent',
                                  color: code === selectedCountry ? '#fff' : '#1a1a1a', // Darker text for better visibility
                                  fontSize: '15px', // Slightly larger font
                                  fontWeight: '500', // Medium weight for better readability
                                  borderBottom: '1px solid #f0f0f0', // Add subtle separator
                                  '&:hover': {
                                    backgroundColor: code === selectedCountry ? '#003663' : '#F0F7FF'
                                  }
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <img 
                                    src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`}
                                    alt={data.name}
                                    style={{ width: 20, height: 15 }}
                                  />
                                  <span>{data.name}</span>
                                </div>
                                <span style={{ color: '#666' }}>{data.dial_code}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Phone Number Input */}
                      <div style={{ flex: 1, position: 'relative' }}>
                        <Form.Control
                          type="tel"
                          value={traveler.phone || ""}
                          onChange={(e) => {
                            // Allow only numbers and common phone characters
                            const value = e.target.value.replace(/[^\d\s\-\+\(\)]/g, '');
                            handleTravelerChange(index, "phone", value);
                          }}
                          style={{
                            ...inputStyle,
                            borderColor: hasError('phone') ? '#dc3545' : '#E5E9F2',
                            borderWidth: hasError('phone') ? '2px' : '1px'
                          }}
                          placeholder={t("Phone number")}
                        />
                        {hasError('phone') && <ErrorIcon />}
                      </div>
                    </div>
                    {hasError('phone') && (
                      <div style={errorMessageStyle}>
                        <span>{t("Please enter a valid phone number")}</span>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label style={formLabelStyle(false)}>{t("Country")}</Form.Label>
                    {/* New simple country dropdown */}
                    <div style={{ position: 'relative' }} data-country-dropdown>
                      <div 
                        onClick={() => setIsAddressCountryOpen(!isAddressCountryOpen)}
                        style={{
                          ...inputStyle,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {traveler.address?.country?.id && (
                            <img 
                              src={`https://flagcdn.com/w20/${traveler.address.country.id.toLowerCase()}.png`}
                              alt="flag"
                              style={{ width: 20, height: 15 }}
                            />
                          )}
                          <span>
                            {traveler.address?.country?.name || t("Select country")}
                          </span>
                        </div>
                        <svg 
                          width="12" 
                          height="12" 
                          viewBox="0 0 16 16"
                          style={{
                            transform: isAddressCountryOpen ? 'rotate(180deg)' : 'rotate(0)',
                            transition: 'transform 0.2s ease'
                          }}
                        >
                          <path 
                            fill="none" 
                            stroke="currentColor" 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M2 5l6 6 6-6"
                          />
                        </svg>
                      </div>
                      
                      {isAddressCountryOpen && (
                        <div 
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '0',
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            zIndex: 9999999999,
                            width: '350px',
                            maxWidth: '90vw',
                            maxHeight: '500px',
                            overflow: 'hidden',
                            border: '1px solid #E5E9F2',
                            marginTop: '4px'
                          }}
                        >
                          {/* Search input */}
                          <div style={{ padding: '16px', borderBottom: '1px solid #eef1f5' }}>
                            <input
                              type="text"
                              value={addressCountrySearchQuery}
                              onChange={(e) => setAddressCountrySearchQuery(e.target.value)}
                              placeholder={t("Search country...")}
                              style={{
                                ...inputStyle,
                                height: '44px',
                                boxShadow: 'none',
                                border: '1px solid #eef1f5'
                              }}
                              autoFocus
                            />
                          </div>
                          
                          {/* Country list */}
                          <div style={{ maxHeight: '440px', overflowY: 'auto' }}>
                            {filteredAddressCountries.map((country) => (
                              <div
                                key={country.cca2}
                                onClick={() => {
                                  // Update the traveler's address country
                                  handleTravelerChange(index, "addressDetails", {
                                    ...traveler.address,
                                    country: {
                                      id: country.cca2,
                                      name: country.name.common
                                    }
                                  });
                                  
                                  setIsAddressCountryOpen(false);
                                  setAddressCountrySearchQuery('');
                                }}
                                style={{
                                  ...nationalityOptionStyle(traveler.address?.country?.id === country.cca2)
                                }}
                              >
                                <span>{country.name.common}</span>
                                <img 
                                  src={`https://flagcdn.com/w20/${country.cca2.toLowerCase()}.png`}
                                  alt={country.name.common}
                                  style={{ width: 20, height: 15 }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label style={formLabelStyle(false)}>{t("City")}</Form.Label>
                    <Form.Control
                      type="text"
                      value={traveler.address?.city?.name || ""}
                      onChange={(e) => handleTravelerChange(index, "addressDetails", { 
                        city: { name: e.target.value } 
                      })}
                      style={inputStyle}
                      className={hasError('city') ? 'error' : ''}
                      placeholder={t("Enter city")}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label style={formLabelStyle(false)}>{t("Zip Code")}</Form.Label>
                    <Form.Control
                      type="text"
                      value={traveler.address?.zipCode || ""}
                      onChange={(e) => handleTravelerChange(index, "addressDetails", { zipCode: e.target.value })}
                      style={inputStyle}
                      className={hasError('zipCode') ? 'error' : ''}
                      placeholder={t("Enter zip code")}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-4">
                    <Form.Label style={formLabelStyle(false)}>{t("Address")}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={traveler.address?.address || ""}
                      onChange={(e) => handleTravelerChange(index, "addressDetails", { address: e.target.value })}
                      style={{...inputStyle, height: 'auto'}}
                      className={hasError('addressDetails') ? 'error' : ''}
                      placeholder={t("Enter full address")}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </div>
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent any default focus behavior
    
    setShowErrors(true);
    
    // Check all required fields for all travelers
    const hasErrors = travelers.some(traveler => {
      const requiredFields = ['name', 'surname', 'birthDate'];
      if (traveler.isLeader) {
        requiredFields.push('email', 'phone', 'address');
      }
      return requiredFields.some(field => !traveler[field]);
    });

    if (hasErrors) {
      toast.error(t("Please fill in all required fields"));
      return;
    }

    setBookingLoading(true);
    setDisableBookingBtn(true);

    try {
      console.log("Starting booking process...");
      
      // 1. Begin Transaction with expiration validation
      const offerIds = rooms.map((r) => r.offerId).filter(Boolean);
      console.log("Beginning transaction with offers:", offerIds);
      
      // ✅ Extract expiration data from selected rooms and route state
      const offersData = selectedRooms?.map(room => ({
        offerId: room.offerId,
        expiresOn: room.expiresOn
      })) || [];
      
      const searchExpiresOn = locationState?.searchExpiresOn;
      
      const beginResponse = await beginPaximumTransaction(
        offerIds,
        currency,
        offersData,
        searchExpiresOn
      );

      console.log("Begin transaction response:", beginResponse);

      if (!beginResponse.success || !beginResponse.transactionId) {
        throw new Error("Failed to begin transaction");
      }

      const transactionId = beginResponse.transactionId;
      console.log("Transaction ID received:", transactionId);

      // 2. Set Reservation Info with MINIMAL required data only
      console.log("Setting reservation info for travelers:", travelers);
      const formattedTravelers = travelers.map((traveler, index) => {
        // Ensure nationality is properly formatted as string
        const nationalityCode = validateNationalityCode(traveler.nationality) || "XK";
        
        console.log(`Traveler ${index + 1} nationality:`, { 
          original: traveler.nationality, 
          code: nationalityCode
        });

        // ✅ MINIMAL REQUIRED DATA ONLY - to avoid validation errors
        const minimalTraveler = {
          travellerId: (index + 1).toString(),
          type: traveler.type,
          title: traveler.title,
          name: traveler.name.toUpperCase(),
          surname: traveler.surname.toUpperCase(),
          isLeader: traveler.isLeader,
          birthDate: traveler.birthDate ? `${traveler.birthDate}T00:00:00` : "1990-01-01T00:00:00",
          nationality: {
            twoLetterCode: nationalityCode
          },
          orderNumber: index + 1,
          gender: Number(traveler.gender) || 0
        };

        // ✅ Add address ONLY for leader and ONLY with essential fields
        if (traveler.isLeader) {
          // Clean phone number - remove any non-digits
          const cleanPhone = (traveler.phone || "").replace(/\D/g, "");
          
          minimalTraveler.address = {
            email: traveler.email || "test@example.com",
            phone: cleanPhone || "123456789" // ✅ Simple phone field instead of complex contactPhone
          };
        }

        return minimalTraveler;
      });

      console.log("🔍 Complete formatted travelers data:", JSON.stringify(formattedTravelers, null, 2));

      // ✅ Add detailed logging for debugging API issues
      console.log("📤 setReservationInfo payload:", {
        transactionId: transactionId,
        travelersCount: formattedTravelers.length,
        reservationNote: reservationNote,
        sampleTraveler: formattedTravelers[0]
      });

      const reservationResponse = await setReservationInfo(
        transactionId, 
        formattedTravelers, 
        reservationNote,
        null,
        beginResponse.expiresOn // ✅ Pass transaction expiration
      );
      console.log("📥 Reservation info response:", reservationResponse);
      console.log("📥 Full response data:", JSON.stringify(reservationResponse, null, 2));

      if (!reservationResponse.success) {
        // ✅ Enhanced error logging
        console.error("❌ setReservationInfo failed:", {
          success: reservationResponse.success,
          error: reservationResponse.error,
          details: reservationResponse.details
        });
        throw new Error(`Failed to set reservation info: ${reservationResponse.error || 'Unknown error'}`);
      }

      // 3. Proceed with payment based on selected method
      console.log("Proceeding with payment method:", paymentMethod);
      if (paymentMethod === "card") {
        console.log("Redirecting to TEB payment...");
        await goToTebPaymentPage(
          overallTotal,
          currency,
          `${window.location.origin}/hotels/paymentOk?transactionId=${transactionId}`,
          `${window.location.origin}/hotels/paymentFailed?transactionId=${transactionId}`,
          setBookingLoading,
          setDisableBookingBtn
        );
      } else if (paymentMethod === "onefor") {
        console.log("Creating OneFor session with data:", {
          transactionId,
          amount: overallTotal,
          currency
        });

        const oneForResponse = await axios.post(
          "https://0nc70p8y1i.execute-api.eu-central-1.amazonaws.com/onefor/session",
          {
            transactionId: transactionId,
            amount: Number(overallTotal),
            currency: currency.toUpperCase(),
            successUrl: `${window.location.origin}/hotels/paymentOk?transactionId=${transactionId}`,
            failUrl: `${window.location.origin}/hotels/paymentFailed?transactionId=${transactionId}`
          },
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
        );

        console.log("OneFor session response:", oneForResponse.data);

        if (!oneForResponse?.data?.session_id) {
          console.error("OneFor session creation failed:", oneForResponse.data);
          throw new Error("Failed to generate OneFor session");
        }

        // Set both session ID and transaction ID
        setOneForSessionId(oneForResponse.data.session_id);
        setTransactionId(transactionId);
        setPaymentStatus("pending");
        
        console.log("OneFor session created successfully:", {
          sessionId: oneForResponse.data.session_id,
          transactionId: transactionId
        });
      }

    } catch (error) {
      console.error("Booking process failed:", error);
      const errorMessage = error.response?.data?.header?.messages?.[0]?.message || 
                          error.response?.data?.header?.messages?.[0]?.detail ||
                          error.message ||
                          "Booking failed. Please try again.";
      toast.error(t(errorMessage));
      setDisableBookingBtn(false);
    } finally {
      setBookingLoading(false);
    }
  };

  const renderStars = (count) => {
    // Use hotelStars from props, fallback to passed count, then default to 4
    const starCount = hotelStars || count || 4;
    const roundedStars = Math.round(starCount);
    
    return (
      <div style={{ display: "flex", marginBottom: "8px" }}>
        {Array.from({ length: roundedStars }).map((_, i) => (
          <img 
            key={i} 
            src={starIcon} 
            alt="star" 
            style={{ width: 16, height: 16, marginRight: 4 }} 
          />
        ))}
      </div>
    );
  };

  // Shtoni funksionin renderCurrency
  const renderCurrency = (currencyCode) => {
    const currencyMap = {
      "EUR": "€",
      "USD": "$",
      "GBP": "£",
      "JPY": "¥",
      "CNY": "¥",
      "RUB": "₽",
      "TRY": "₺",
      "INR": "₹",
      "CHF": "CHF",
      "CAD": "CA$",
      "AUD": "A$"
    };

    // Return the icon if available, otherwise return the original code
    return currencyMap[currencyCode] || currencyCode;
  };

  // Add polling function
  const pollPaymentStatus = async (sessionId, transactionId) => {
    try {
      const response = await axios.get(
        `https://0nc70p8y1i.execute-api.eu-central-1.amazonaws.com/onefor/session/${sessionId}`
      );
      
      console.log("Polling response:", response.data);
      
      if (response.data.success && response.data.data) {
        const sessionData = response.data.data;
        
        if (sessionData.isPaid && sessionData.bookingConfirmed) {
          setPaymentStatus("success");
          // Payment confirmed and booking committed - redirect to success page
          window.location.href = `/hotels/paymentOk?transactionId=${transactionId}&sessionId=${sessionId}`;
        } else if (sessionData.status === "failed") {
          setPaymentStatus("failed");
          window.location.href = `/hotels/paymentFailed?sessionId=${sessionId}`;
        } else if (sessionData.isPaid && sessionData.commitStatus === 'failed') {
          setPaymentStatus("commit_failed");
          // Payment successful but booking commit failed
          toast.error(t("Payment successful but booking confirmation failed. Please contact support."));
        }
        // If status is still 'pending', continue polling
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPaymentStatus("error");
    }
  };

  // Update useEffect to include transactionId
  useEffect(() => {
    let pollInterval;
    
    if (oneForSessionId && paymentMethod === "onefor") {
      pollInterval = setInterval(() => {
        pollPaymentStatus(oneForSessionId, transactionId);
      }, 3000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [oneForSessionId, paymentMethod, transactionId]);

  // Add the styles to the component
  useEffect(() => {
    const styleElement = document.createElement('div');
    styleElement.innerHTML = datePickerCustomStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Update the input style to include error state
  const getInputStyle = (hasError) => ({
    ...inputStyle,
    border: hasError ? '1px solid #dc3545' : '1px solid #E5E9F2',
    '&:focus': {
      ...inputStyle['&:focus'],
      borderColor: hasError ? '#dc3545' : '#003663',
    }
  });

  // Update the calendar icon style
  const calendarIconStyle = (hasError) => ({
    position: "absolute",
    right: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    color: hasError ? "#dc3545" : "#003663",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    zIndex: 1
  });

  // Initialize all dropdowns as closed
  const [openNationalityDropdowns, setOpenNationalityDropdowns] = useState({});
  const nationalityDropdownRefs = useRef({});

  // Reset all dropdowns when component mounts
  useEffect(() => {
    setOpenNationalityDropdowns({});
  }, []);

  // Enhanced click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside nationality dropdowns
      const shouldCloseNationality = !Object.values(nationalityDropdownRefs.current).some(ref => 
        ref && ref.contains(event.target)
      );

      // Check if click is outside address country dropdown (new simple dropdown)
      const countryDropdownContainer = event.target.closest('[data-country-dropdown]');
      const shouldCloseAddressCountry = !countryDropdownContainer;

      if (shouldCloseNationality) {
        setOpenNationalityDropdowns({});
      }

      if (shouldCloseAddressCountry) {
        setIsAddressCountryOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleNationalityDropdown = (index, event) => {
    event.stopPropagation(); // Prevent event bubbling
    
    // Calculate position for fixed positioning
    const rect = event.currentTarget.getBoundingClientRect();
    const dropdown = nationalityDropdownRefs.current[index];
    
    if (dropdown) {
      dropdown.style.top = `${rect.bottom + 8}px`;
      dropdown.style.left = `${rect.left}px`;
      
      // Adjust if dropdown would go off screen
      const dropdownHeight = 500; // maxHeight
      const viewportHeight = window.innerHeight;
      
      if (rect.bottom + dropdownHeight > viewportHeight) {
        // Position above the input if there's not enough space below
        dropdown.style.top = `${rect.top - dropdownHeight - 8}px`;
      }
      
      // Adjust horizontal position if needed
      const dropdownWidth = 350;
      const viewportWidth = window.innerWidth;
      
      if (rect.left + dropdownWidth > viewportWidth) {
        dropdown.style.left = `${viewportWidth - dropdownWidth - 16}px`;
      }
    }
    
    setOpenNationalityDropdowns(prev => {
      // Close all other dropdowns first
      const newState = {};
      // Toggle the clicked dropdown
      newState[index] = !prev[index];
      return newState;
    });
  };

  // Add state for phone code dropdowns
  const [openPhoneCodeDropdowns, setOpenPhoneCodeDropdowns] = useState({});
  const phoneCodeDropdownRefs = useRef({});
  const [phoneCodeSearchQuery, setPhoneCodeSearchQuery] = useState('');
  
  // Add CSS to ensure dropdowns are on top of everything
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .nationality-select-dropdown {
        z-index: 9999999999 !important;
        position: fixed !important;
      }
      .nationality-select-container {
        z-index: 9999999998 !important;
        position: relative !important;
      }
      /* Override any other z-index values */
      .nationality-select-dropdown,
      .nationality-select-dropdown * {
        z-index: 9999999999 !important;
      }
      /* Ensure passenger details don't interfere */
      .passenger-details-container,
      .passenger-details-container * {
        z-index: 1 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Filter phone codes based on search
  const filteredPhoneCodes = useMemo(() => {
    const priorityCountries = ['XK', 'AL']; // Kosovo and Albania country codes
    
    return Object.entries(extendedCountryTelData)
      .filter(([code, data]) => 
        data.name.toLowerCase().includes(phoneCodeSearchQuery.toLowerCase()) ||
        data.dial_code.includes(phoneCodeSearchQuery) ||
        code.toLowerCase().includes(phoneCodeSearchQuery.toLowerCase())
      )
      .sort((a, b) => {
        const aIndex = priorityCountries.indexOf(a[0]);
        const bIndex = priorityCountries.indexOf(b[0]);
        
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        
        return a[1].name.localeCompare(b[1].name);
      });
  }, [phoneCodeSearchQuery]);

  // Add click outside handler for phone code dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside phone code dropdowns
      const shouldClosePhoneCodes = !Object.values(phoneCodeDropdownRefs.current).some(ref => 
        ref && ref.contains(event.target)
      );

      // Don't close if clicking on address country dropdown
      const isAddressCountryClick = event.target.closest('.nationality-select-container');

      if (shouldClosePhoneCodes && !isAddressCountryClick) {
        setOpenPhoneCodeDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // No positioning needed for absolute positioned dropdowns

  const togglePhoneCodeDropdown = (index, event) => {
    event.stopPropagation();
    
    setOpenPhoneCodeDropdowns(prev => {
      const newState = {};
      newState[index] = !prev[index];
      return newState;
    });
  };

  const handlePhoneCodeSelect = (countryCode, index, traveler) => {
    const dialCode = extendedCountryTelData[countryCode]?.dial_code.replace("+", "") || "49";
    // Keep the existing phone number, just update the country code
    if (traveler.phone) {
      handleTravelerChange(index, "phone", traveler.phone);
    }
    setOpenPhoneCodeDropdowns({});
    setPhoneCodeSearchQuery('');
  };

  const validateForm = () => {
    setShowErrors(true);
    
    // Check all required fields for all travelers
    const hasErrors = travelers.some(traveler => {
      const requiredFields = ['name', 'surname', 'birthDate'];
      if (traveler.isLeader) {
        requiredFields.push('email', 'phone', 'address');
      }
      return requiredFields.some(field => !traveler[field]);
    });

    if (hasErrors) {
      toast.error(t("Please fill in all required fields"));
      return false;
    }

    return true;
  };



  /** Helper function to determine refund status and get appropriate icon */
  const getRefundStatus = (room) => {
    // Check if room has cancellation policies
    const hasCancellationPolicies = room.cancellationPolicies && room.cancellationPolicies.length > 0;
    
    // Use the API's isRefundable field directly, or fall back to cancellation policy check
    const isRefundable = room.isRefundable !== undefined ? room.isRefundable : hasCancellationPolicies;
    
    return {
      icon: isRefundable ? RefundIcon : NonRefundIcon,
      isRefundable: isRefundable
    };
  };

  return (
    <>
      <Header translation={t} translationContext={{ t }} />
      

      
      <div
        style={{
          width: "100%",
          backgroundColor: "#f8f9fa",
          paddingTop: 10,
          paddingBottom: 10,
          marginBottom: "40px"
        }}
      >
        <Form onSubmit={handleSubmit}>
          <Row className="mb-5">
            <Col
              md={5}
              className="order-1 order-md-2 mb-5 mb-md-0"
            >
              <Card className="shadow-sm rounded overflow-hidden position-relative" style={{ 
                maxWidth: "400px", 
                margin: "0 auto", 
                backgroundColor: "#fff",
                boxShadow: "0 8px 16px rgba(0, 0, 0, 0.15)" 
              }}>
                {thumbnail ? (
                  <div style={{ position: "relative", height: "180px" }}>
                    <div style={{ 
                      position: "absolute", 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      bottom: 0,
                      backgroundImage: `url(${thumbnail})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      filter: "brightness(0.7)",
                      zIndex: 1
                    }} />
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 54, 99, 0.3)", // Light blue with 30% opacity
                      zIndex: 2
                    }} />
                    <div
                      style={{
                        position: "relative",
                        padding: "16px 12px",
                        color: "white",
                        zIndex: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                      }}
                    >
                      {renderStars()}
                      <div
                        style={{
                          fontWeight: "bold",
                          fontSize: 24,
                          marginBottom: 8,
                          textShadow: "0 2px 6px rgba(0,0,0,0.8)",
                          letterSpacing: "0.5px"
                        }}
                      >
                        {hotelName || "Hotel Name Not Available"}
                      </div>
                      <div style={{ 
                        fontSize: 14, 
                        textShadow: "0 2px 4px rgba(0,0,0,0.7)",
                        fontWeight: "500"
                      }}>
                        {hotelLocation || "Unknown location"}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Fallback imazh nëse thumbnail nuk është i disponueshëm
                  <div style={{ 
                    position: "relative", 
                    height: "180px", 
                    backgroundColor: "#003663",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    padding: "16px 12px",
                    color: "white"
                  }}>
                    {renderStars()}
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: 24,
                        marginBottom: 8,
                        textShadow: "0 2px 6px rgba(0,0,0,0.8)",
                        letterSpacing: "0.5px"
                      }}
                    >
                      {hotelName || "Hotel Name Not Available"}
                    </div>
                    <div style={{ 
                      fontSize: 14, 
                      textShadow: "0 2px 4px rgba(0,0,0,0.7)",
                      fontWeight: "500"
                    }}>
                      {hotelLocation || "Unknown location"}
                    </div>
                  </div>
                )}

                <Card.Body style={{ 
                  padding: "16px 12px",
                  backgroundColor: "#fff",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <div style={blendOverlayStyle} />
                  <div style={{position: "relative", zIndex: 2}}>
                    <div className="d-flex justify-content-between mb-4" style={{ gap: "12px" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{fontSize: "1rem", fontWeight: 600, color: primaryColor, marginBottom: "5px", textAlign: "left", paddingLeft: "12px"}}>
                          {t("Check-in")}
                        </div>
                        <div style={{...dateCardStyle, padding: "12px", margin: "0", textAlign: "left"}}>
                          <div style={blendOverlayStyle} />
                          <div style={{position: "relative", zIndex: 2}}>
                            <div style={{...dateLabelStyle, fontSize: "1rem", textAlign: 'left'}}>{formatDate(effectiveCheckIn).weekday}</div>
                            <div style={{...dateDetailStyle, fontSize: "1rem", marginTop: "8px", textAlign: 'left'}}>
                              {formatDate(effectiveCheckIn).day} {formatDate(effectiveCheckIn).month} {formatDate(effectiveCheckIn).year}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{fontSize: "1rem", fontWeight: 600, color: primaryColor, marginBottom: "5px", textAlign: "left", paddingLeft: "12px"}}>
                          {t("Check-out")}
                        </div>
                        <div style={{...dateCardStyle, padding: "12px", margin: "0", textAlign: "left"}}>
                          <div style={blendOverlayStyle} />
                          <div style={{position: "relative", zIndex: 2}}>
                            <div style={{...dateLabelStyle, fontSize: "1rem", textAlign: 'left'}}>{formatDate(effectiveCheckOut).weekday}</div>
                            <div style={{...dateDetailStyle, fontSize: "1rem", marginTop: "8px", textAlign: 'left'}}>
                              {formatDate(effectiveCheckOut).day} {formatDate(effectiveCheckOut).month} {formatDate(effectiveCheckOut).year}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {rooms.map((room, idx) => (
                      <div
                        key={idx}
                        className="mb-3 p-4"
                        style={{
                          backgroundColor: "#fff",
                          borderRadius: "0 0 12px 12px",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                          borderTop: "none",
                          position: "relative",
                          overflow: "hidden"
                        }}
                      >
                        <div style={blendOverlayStyle} />
                        <div style={{position: "relative", zIndex: 2}}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "8px" }}>
                            <strong style={{ ...textStyle, fontSize: "1.1rem", fontWeight: 600, textAlign: 'left' }}>
                              {t("Room")} {idx + 1}: {room?.assignedCriteria?.adult || 0} {room?.assignedCriteria?.adult === 1 ? t("Adult") : t("Adults")}
                            </strong>
                            {/* <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <img 
                                src={getRefundStatus(room).icon} 
                                alt={getRefundStatus(room).isRefundable ? "Refundable" : "Non-refundable"}
                                style={{ width: "48px", height: "48px" }}
                                title={getRefundStatus(room).isRefundable ? t("Refundable") : t("Non-refundable")}
                              />
                            </div> */}
                          </div>
                          <div
                            style={{
                              color: primaryColor,
                              fontSize: "1rem",
                              fontWeight: 500,
                              display: "flex",
                              flexDirection: "column",   
                              gap: "8px",
                              textAlign: 'left'
                            }}
                          >
                            <span style={{ fontSize: "1rem", fontWeight: 600, textAlign: 'left', color: primaryColor }}>
                              {extractRoomName(room)}
                            </span>
                            
                            <span style={{ fontSize: "1rem", textAlign: 'left', fontWeight: 600 }}>
                              {(() => {
                                const boardType = room.boardType || room.rooms?.[0]?.boardName || defaultRoom.boardType;
                                // Check if it looks like a translation key (lowercase with underscores)
                                if (boardType && typeof boardType === 'string' && /^[a-z_]+$/.test(boardType)) {
                                  return t(boardType);
                                }
                                // Otherwise render directly
                                return boardType;
                              })()}
                            </span>
                            
                            {room?.assignedCriteria?.childAges && room.assignedCriteria.childAges.length > 0 && (
                              <div style={{ fontSize: "1rem", textAlign: 'left' }}>
                                <div>{room.assignedCriteria.childAges.length} {room.assignedCriteria.childAges.length === 1 ? t("Child") : t("Children")}</div>
                                <div style={{ fontSize: "0.9rem", color: "#666", marginTop: "4px" }}>
                                  {t("Ages")}: {room.assignedCriteria.childAges.join(", ")}
                                </div>
                              </div>
                            )}
                            
                            {effectiveNights > 0 && (
                              <div style={{ fontSize: "14px", color: "#003663", fontWeight: 600 }}>
                                {effectiveNights} {effectiveNights === 1 ? t("Night") : t("Nights")}
                              </div>
                            )}
                            
                            {/* Refund status icon with text */}
                              <div style={{ 
                                display: "flex", 
                                alignItems: "center", 
                                marginTop: "12px",
                                gap: "12px"
                              }}>
                                <img 
                                  src={getRefundStatus(room).isRefundable ? RefundIcon : NonRefundIcon} 
                                  alt={getRefundStatus(room).isRefundable ? t("refundable") : t("non_refundable")}
                                  style={{ width: "30px", height: "30px" }}
                                  title={getRefundStatus(room).isRefundable ? t("refundable") : t("non_refundable")}
                                />
                                <span style={{
                                  color: "#003663",
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  textAlign: "left"
                                }}>
                                  {getRefundStatus(room).isRefundable ? t("Refundable") : t("Non-refundable")}
                                </span>
                              </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {discountAmount > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 p-2" style={{ borderTop: "1px solid #eee", paddingTop: "12px" }}>
                        <span style={{ fontSize: "1rem", fontWeight: 500, color: primaryColor }}>{t("Subtotal")}:</span>
                        <span style={{ fontSize: "1.2rem", fontWeight: 600, color: primaryColor }}>
                          {Math.floor(baseTotal)}
                          <span style={{ fontSize: "0.9rem", verticalAlign: "super" }}>
                            .{(baseTotal % 1).toFixed(2).split(".")[1]}
                          </span>{" "}
                          {renderCurrency(currency)}
                        </span>
                      </div>
                    )}
                    
                    {discountAmount > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-2 p-2">
                        <span style={{ fontSize: "1rem", fontWeight: 500, color: "#28a745" }}>{t("Discount")}:</span>
                        <span style={{ fontSize: "1.2rem", fontWeight: 600, color: "#28a745" }}>
                          -{Math.floor(discountAmount)}
                          <span style={{ fontSize: "0.9rem", verticalAlign: "super" }}>
                            .{(discountAmount % 1).toFixed(2).split(".")[1]}
                          </span>{" "}
                          {renderCurrency(currency)}
                        </span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mt-4 p-2" style={{ borderTop: discountAmount > 0 ? "2px solid #003663" : "none", paddingTop: discountAmount > 0 ? "12px" : "0" }}>
                      <span style={{ fontSize: "1.1rem", fontWeight: 600, color: primaryColor }}>{t("Total Price")}:</span>
                      <span style={{ fontSize: "1.8rem", fontWeight: 700, color: primaryColor }}>
                        {Math.floor(overallTotal)}
                        <span style={{ fontSize: "1rem", verticalAlign: "super" }}>
                          .{(overallTotal % 1).toFixed(2).split(".")[1]}
                        </span>{" "}
                        {renderCurrency(currency)}
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col
              md={7}
              className="order-2 order-md-1"
              style={textStyle}
            >
              <div className="d-flex align-items-start gap-4 mb-5 p-4" style={{ 
                backgroundColor: "#fff",
                border: "none",
                borderRadius: "0 0 12px 12px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                borderTop: "none",
                position: "relative",
                overflow: "hidden"
              }}>
                <img
                  src={passportIcon}
                  alt="Passport"
                  style={{ width: 54 }}
                />
                <div>
                  <strong style={{ fontSize: "1.2rem" }}>{t("Check passenger details")}</strong>
                  <div style={{ fontSize: "1rem", color: primaryColor, marginTop: "6px" }}>
                    {t("Use all given names and surnames exactly as they appear on your passport / ID to avoid departure complications")}
                  </div>
                </div>
              </div>

              {/* Travelers Forms */}
              {travelers.map((traveler, index) => renderTravelerForm(traveler, index))}

              {/* Promo Code Section */}
              <div className="mb-5" style={{ backgroundColor: "transparent" }}>
                <div style={{ paddingLeft: 20, paddingRight: 20}}>
                  <div className="mb-4">
                    <Form.Check
                      type="checkbox"
                      id="promoCodeCheckbox"
                      checked={promoCode}
                      onChange={(e) => setPromoCode(e.target.checked)}
                      style={{ color: primaryColor }}
                      label={
                        <span style={{ color: primaryColor, fontSize: "17px" }}>
                          {t("Add promo code")}
                        </span>
                      }
                    />
                  </div>
                  
                  {promoCode && (
                    <div className="mb-4">
                      <div style={{ 
                        backgroundColor: "#fff",
                        borderRadius: "0 0 12px 12px",
                        padding: "24px", 
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        borderTop: "none",
                        position: "relative",
                        overflow: "hidden"
                      }}>
                        <div style={blendOverlayStyle} />
                        <div style={{position: "relative", zIndex: 2}}>
                          <div style={{ 
                            display: "flex", 
                            gap: "16px", 
                            alignItems: "flex-end",
                            flexDirection: isMobile ? "column" : "row"
                          }}>
                            <div style={{ flex: 1, minWidth: "200px" }}>
                              <Form.Label style={{ color: primaryColor, fontWeight: "500", marginBottom: "8px" }}>
                                {t("Promo Code")}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                value={promoCodeValue}
                                onChange={(e) => setPromoCodeValue(e.target.value)}
                                placeholder={t("Enter promo code")}
                                style={{
                                  ...inputStyle,
                                  borderColor: voucherInfo?.code === 400 ? '#dc3545' : '#E5E9F2'
                                }}
                              />
                            </div>
                            <Button
                              variant="contained"
                              disabled={!promoCodeValue || voucherInfo?.loading}
                              onClick={async () => {
                                setVoucherInfo(prev => ({ ...prev, loading: true }));
                                try {
                                  const applyVoucherResponse = await axios.post(
                                    'https://7p06sk4uo1.execute-api.eu-central-1.amazonaws.com/dev/apply-voucher',
                                    { voucher: promoCodeValue },
                                    { headers: { 'Content-Type': 'application/json' } }
                                  );

                                  if (applyVoucherResponse.data.statusCode === 400) {
                                    setVoucherInfo({
                                      message: applyVoucherResponse.data.errorMessage,
                                      code: 400,
                                      loading: false,
                                    });
                                  }

                                  if (applyVoucherResponse.data.statusCode === 200 && applyVoucherResponse.data.amount) {
                                    setVoucherInfo({
                                      message: "Voucher applied successfully!",
                                      code: 200,
                                      loading: false,
                                    });
                                    setDiscountAmount(Number(applyVoucherResponse.data.amount));
                                  }
                                } catch (err) {
                                  setVoucherInfo({
                                    message: "Something went wrong!",
                                    code: 500,
                                    loading: false,
                                  });
                                }
                              }}
                              style={{
                                borderRadius: "30px",
                                backgroundColor: "#FBD316",
                                color: primaryColor,
                                border: "none",
                                padding: "12px 24px",
                                fontWeight: "600",
                                height: "54px",
                                minWidth: "120px",
                                transition: "all 0.2s ease-in-out",
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                cursor: !promoCodeValue || voucherInfo?.loading ? "not-allowed" : "pointer",
                                opacity: !promoCodeValue || voucherInfo?.loading ? 0.7 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (!(!promoCodeValue || voucherInfo?.loading)) {
                                  e.currentTarget.style.transform = "scale(1.05)";
                                  e.currentTarget.style.boxShadow = "0 8px 16px rgba(0, 0, 0, 0.2)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
                              }}
                            >
                              {voucherInfo?.loading ? (
                                <div style={{ 
                                  width: "20px", 
                                  height: "20px", 
                                  border: "2px solid #fff", 
                                  borderTop: "2px solid transparent", 
                                  borderRadius: "50%", 
                                  animation: "spin 1s linear infinite" 
                                }} />
                              ) : (
                                t("APPLY")
                              )}
                            </Button>
                          </div>
                          
                          {voucherInfo?.message && (
                            <div style={{
                              marginTop: "16px",
                              padding: "12px 16px",
                              borderRadius: "8px",
                              backgroundColor: voucherInfo.code === 200 ? "#d4edda" : "#f8d7da",
                              color: voucherInfo.code === 200 ? "#155724" : "#721c24",
                              border: `1px solid ${voucherInfo.code === 200 ? "#c3e6cb" : "#f5c6cb"}`,
                              fontSize: "14px",
                              fontWeight: "500"
                            }}>
                              {voucherInfo.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Reservation Note and Company Information Section */}
              <div className="mb-5" style={{ backgroundColor: "transparent" }}>
                <div style={{ paddingLeft: 20, paddingRight: 20}}>
                  {/* Reservation Note */}
                  <div className="mb-4">
                    <div style={{ 
                      backgroundColor: "#fff",
                      borderRadius: "0 0 12px 12px",
                      padding: "24px", 
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                      borderTop: "none",
                      position: "relative",
                      overflow: "hidden"
                    }}>
                      <div style={blendOverlayStyle} />
                      <div style={{position: "relative", zIndex: 2}}>
                        <Form.Group className="mb-4">
                          <Form.Label style={{ color: primaryColor, fontWeight: "500", marginBottom: "8px" }}>
                            {t("Reservation Note")} ({t("Optional")})
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={reservationNote}
                            onChange={(e) => setReservationNote(e.target.value)}
                            placeholder={t("Add any special requests or notes for your reservation...")}
                            style={{
                              ...inputStyle,
                              height: 'auto',
                              resize: 'vertical'
                            }}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="mb-5" style={{ backgroundColor: "transparent" }}>
                <div style={{ paddingLeft: 20, paddingRight: 20}}>

                <h5 className="mb-4" style={{ color: primaryColor }}>
                  {t("How would you like to pay?")}
                </h5>
                
                <p className="small mb-4" style={{ color: primaryColor }}>
                  {t(
                    "The data transmitted is coded using SSL (Secure Socket Layer) technology: the session in progress on this site is secure and protected by maximum privacy."
                  )}
                </p>
                  </div>

                <div style={{ 
                  backgroundColor: "#fff",
                  borderRadius: "0 0 12px 12px",
                  padding: "25px", 
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  marginBottom: "20px",
                  borderTop: "none",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <div style={blendOverlayStyle} />
                  <div style={{position: "relative", zIndex: 2}}>
                    {/* Credit/Debit Card Payment Tab */}
                    <div style={{...paymentTabStyle, marginBottom: "20px", backgroundColor: "#f9f9f9", boxShadow: "none"}}>
                      <div style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: isMobile ? "flex-start" : "space-between",
                        alignItems: isMobile ? "flex-start" : "center",
                        gap: isMobile ? "12px" : "0"
                      }}>
                        <Form.Check
                          type="radio"
                          name="paymentMethod"
                          id="cardPaymentRadio"
                          label={t("Credit or debit card")}
                          value="card"
                          checked={paymentMethod === "card"}
                          onChange={() => setPaymentMethod("card")}
                          className="mb-0"
                          style={{ color: primaryColor }}
                        />
                        <div className="d-flex align-items-center" style={{ 
                          gap: "1rem", 
                          paddingLeft: isMobile ? "20px" : "40px",
                          paddingRight: isMobile ? 0 : 10,
                          justifyContent: isMobile ? "flex-start" : "flex-end"
                        }}>
                          <img src={visaLogo} alt="Visa" style={{ height: 38}} />
                          <MasterCardLogo style={{
                            height: isMobile ? 60 : 44,
                            width: isMobile ? 60 : 44,
                            marginTop: isMobile ? 6 : 0
                          }} />
                          <TebLogo style={{
                            height: isMobile ? 70 : 44,
                            width: isMobile ? 70 : 44,
                            marginTop: isMobile ? 4 : 0
                          }} />
                        </div>
                      </div>
                    </div>

                    {/* OneFor Payment Tab */}
                    <div style={{...paymentTabStyle, marginBottom: "20px", backgroundColor: "#f9f9f9", boxShadow: "none"}}>
                      <div style={{
                        display: "flex",
                        flexDirection: "row", // Always horizontal for OneFor since it's a short name
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "0"
                      }}>
                        <Form.Check
                          type="radio"
                          name="paymentMethod"
                          id="oneforPaymentRadio"
                          label={
                            <span>
                              {t("OneFor")}{" "}
                              <a 
                                href="https://onefor.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: '0.85rem', color: primaryColor, textDecoration: 'underline' }}
                              >
                                {t("Learn More")}
                              </a>
                            </span>
                          }
                          value="onefor"
                          checked={paymentMethod === "onefor"}
                          onChange={() => setPaymentMethod("onefor")}
                          className="mb-0"
                          style={{ color: primaryColor }}
                        />
                        <img src={oneForLogo} alt="OneFor" style={{ 
                          height: 28, 
                          paddingLeft: "40px",
                          paddingRight: 10 
                        }} />
                      </div>
                    </div>

                    {paymentMethod === "card" && (
                      <div 
                        className="small mt-4 mb-4 p-3 rounded" 
                        style={{ color: primaryColor, backgroundColor: "#f9f9f9", border: "1px solid #eee" }}
                        dangerouslySetInnerHTML={{ __html: t("starcard_installment") }}
                      />
                    )}

                    {paymentMethod === "onefor" && oneForSessionId && (
                      <div className="text-center my-4 p-4 rounded" 
                           style={{ 
                             color: primaryColor, 
                             backgroundColor: "#f9f9f9", 
                             border: "1px solid #eee",
                             boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
                           }}>
                        <h5 className="mb-3">{t("Scan this QR code to proceed with payment")}</h5>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          marginBottom: '20px'
                        }}>
                          <QRCodeSVG
                            value={`https://web.app.onefor.com/web/merchant?sessionId=${oneForSessionId}`}
                            size={200}
                            level="H"
                            includeMargin={true}
                          />
                        </div>
                        <p className="mt-3 mb-2">
                          <a 
                            href={`https://web.app.onefor.com/web/merchant?sessionId=${oneForSessionId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              color: primaryColor, 
                              textDecoration: "underline",
                              fontWeight: "500"
                            }}
                          >
                            {t("Or click here to open payment page")}
                          </a>
                        </p>
                        {paymentStatus === "pending" && (
                          <div className="mt-3 p-2" style={{ backgroundColor: "#fff", borderRadius: "8px" }}>
                            <p className="mb-0" style={{ color: "#666" }}>
                              {t("Waiting for payment confirmation...")}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {paymentMethod === "onefor" && !oneForSessionId && (
                      <div className="small mt-4 mb-4 p-3 rounded" style={{ color: primaryColor, backgroundColor: "#f9f9f9", border: "1px solid #eee" }}>
                        {t("Scan the QR code that will be generated to proceed with your OneFor payment.")}{" "}
                        <a 
                          href="https://onefor.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: primaryColor, textDecoration: 'underline' }}
                        >
                          {t("Learn More")}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-5" style={{ backgroundColor: "transparent", paddingLeft: 20, paddingRight: 20 }}>
                <Form.Check
                  type="checkbox"
                  id="consentCheckbox"
                  className="mb-4 p-4"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  required
                  style={{ color: primaryColor, backgroundColor: "transparent", borderRadius: "8px" }}
                  label={
                    <span style={{ color: primaryColor }}>
                      {t("I accept ")}
                      <a href="/terms-conditions" target="_blank" rel="noopener noreferrer" style={{ color: '#0156B3', textDecoration: 'none' }}>
                        {t("Terms & Conditions")}
                      </a>
                      {t(" and ")}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#0156B3', textDecoration: 'none' }}>
                        {t("Privacy Policy")}
                      </a>
                    </span>
                  }
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="submit"
                  disabled={disableBookingBtn || !consentChecked}
                  style={{
                    backgroundColor: "#FBD316",
                    color: primaryColor,
                    border: "none",
                    borderRadius: "30px",
                    padding: "16px 32px",
                    fontWeight: "800",
                    fontSize: "1.1rem",
                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    height: "56px",
                    minWidth: "240px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 12px 20px rgba(0, 0, 0, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 8px 16px rgba(0, 0, 0, 0.15)";
                  }}
                >
                  <span>{t("BOOK NOW")}</span>
                  <span style={{ display: "flex", alignItems: "flex-start", fontWeight: 800 }}>
                    <span style={{ fontSize: "1.15rem", marginTop: "1px" }}>
                      {Math.floor(overallTotal)}
                    </span>
                    <span style={{ fontSize: "0.9rem", lineHeight: "1.6", marginLeft: "1px", verticalAlign: "super" }}>
                      .{(overallTotal % 1).toFixed(2).split(".")[1]}
                    </span>
                    &nbsp;
                    <span style={{ fontSize: "1.3rem" }}>{renderCurrency(currency)}</span>
                  </span>
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  );
};

export default BookingPage;